import { HTMLAttributes, MutableRefObject, ReactNode, Ref, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import classNames from "classnames";
import equal from "fast-deep-equal";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import {
  Cell,
  Column as BaseColumn,
  ColumnInstance,
  ColumnInterface,
  ColumnInterfaceBasedOnValue,
  Filters,
  Renderer,
  Row,
  SortingRule,
  TableInstance,
  useColumnOrder,
  useFilters,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { genericMemo, PathResult, useDebounce, useRefEffect } from "../../../utils";
import { DisabledContextProvider } from "../../form";
import {
  AppConfig,
  AppConfigContext,
  ExportToExcelHookProps,
  ExportToExcelProps,
  MimicExcelColumn,
  useExportToExcel,
  UserSettingContext,
} from "../../generic";
import { SnackbarContext, SnackbarMessagePropsExt } from "../Snackbar/SnackbarContext";
import BulkOps from "./components/BulkOps";
import ClearFilters from "./components/ClearFilters/ClearFilters";
import ExportToExcel from "./components/ExportToExcel";
import Pager from "./components/Pager";
import { SelectionActions, TableSelectionActionProps } from "./components/SelectionActions";
import SetDefaultFilters from "./components/SetDefaultFilters";
import Settings from "./components/Settings";
import Table from "./components/Table";
import { BodyRowProps } from "./components/Table/Body/BodyRow";
import SortingContext from "./components/Table/contexts/SortingContext";
import VisibleColumnsContext from "./components/Table/contexts/VisibleColumnsContext";
import TableActions from "./components/TableActions";
import { useCustomTableFilters } from "./plugin-hooks/useCustomTableFilters";
import { useGridLayout } from "./plugin-hooks/useGridLayout";
import { useResizeColumns } from "./plugin-hooks/useResizeColumns";
import { useRowSelect } from "./plugin-hooks/useRowSelect";
import { SelectionColumn } from "./columns";

import styles from "./style.module.scss";

type FetchTableDataProps<T extends object> = { pageIndex: number, pageSize: number, sortBy: SortingRule<T>[], filters: Filters<T> };
export type FetchTableData = <T extends object>(props: FetchTableDataProps<T>) => void;

type BaseColumnType<T extends object> = (
  BaseColumn<T>
  | (ColumnInterface<T> & { accessor: PathResult<any> })
  | (ColumnInterfaceBasedOnValue<T> & { accessor: PathResult<any> }));

export type BaseColumnTypeAdditionalProps<T extends object> = {
  Footer?: Renderer<any>,
  footerAccessor?: (footerData: any, column: ColumnInstance<T>) => any,
  getRowProps?: (row: Row<T>) => Partial<BodyRowProps<T>> | undefined,
  hideInSettings?: boolean,
  hiddenByDefault?: boolean,
  freezeOnScroll?: boolean,
  getCellProps?: (cell: Cell<T, any> | undefined, type: "header" | "body" | "footer") => HTMLAttributes<HTMLDivElement>,
  filterInputDisabled?: boolean,
};

type ExcelColumnProps<T> = {
  canExportToExcel?: boolean
  excelProps?: Partial<MimicExcelColumn<T>> & { valueModifier?: (value: any, context: AppConfig) => string | number | Date | null | undefined }
}

export type Column<T extends object> = BaseColumnType<T> & BaseColumnTypeAdditionalProps<T> & ExcelColumnProps<T>
export type ExcludedColumn = { id?: string, accessor?: PathResult<any> | string }

export interface TableProps<T extends object> {
  hideSettings?: boolean,
  tableId: string,
  columns: Column<T>[],
  excludedColumns?: ExcludedColumn[],
  data: T[],
  fetchData?: FetchTableData,
  loading?: boolean,
  totalCount: number,
  pageCount: number,
  allowRowSelect?: boolean,
  showFooter?: boolean,
  getRowId?: (obj: T, index: number) => string
  getRowProps?: (row: Row<T>) => Partial<BodyRowProps<T>> | undefined,
  defaultColumn?: Partial<Column<T>>,
  debounceTimeout?: number,
  defaultSortBy?: SortingRule<T>[],
  defaultFilters?: Filters<T>,
  unclearableFilters?: Filters<T>,
  defaultIsBulkOpsEnabled?: boolean,
  className?: string,
  tableClassName?: string,
  selectionActionsClassName?: string,
  selectionActions?: (props: TableSelectionActionProps) => ReactNode,
  selectionShowSelectedItemsCount?: boolean,
  selectionShowSelectedAll?: boolean,
  selectionEntityName?: string,
  tableActions?: ReactNode,
  disablePager?: boolean,
  disabled?: boolean,
  keepFetchingParams?: boolean,
  manualFilters?: boolean,
  manualSortBy?: boolean,
  autoResetSortBy?: boolean,
  allowExportToExcel?: boolean,
  createExcelProps?: ExportToExcelProps,
  toExcelGetItems?: (messageProps: SnackbarMessagePropsExt) => T[] | Promise<T[]>,
  tableRef?: Ref<TableInstance<T>> | MutableRefObject<TableInstance<T>>,
  onSelectedRowCountChanged?: (count: number) => void,
  onColumnsChanged?: (columns: (ColumnInstance<T> & Column<T>)[]) => void,
  allwaysShowSelectionActions?: boolean
}


export const TableContainer = genericMemo(<T extends object>(
  {
    hideSettings = false,
    columns: declaredColumns,
    data,
    fetchData,
    loading,
    totalCount,
    pageCount: controlledPageCount,
    allowRowSelect,
    getRowId = (originalRow, relativeIndex) => (originalRow as any)?.id || relativeIndex,
    showFooter,
    defaultColumn,
    debounceTimeout = 0,
    tableId,
    defaultSortBy = [],
    defaultFilters,
    defaultIsBulkOpsEnabled = false,
    autoResetSortBy = true,
    className,
    tableClassName,
    selectionActionsClassName,
    selectionActions,
    selectionShowSelectedItemsCount,
    selectionShowSelectedAll,
    selectionEntityName,
    tableActions,
    disablePager = false,
    disabled,
    keepFetchingParams = true,
    manualFilters = true,
    manualSortBy = true,
    allowExportToExcel = true,
    createExcelProps,
    toExcelGetItems,
    excludedColumns,
    tableRef,
    onSelectedRowCountChanged,
    onColumnsChanged,
    unclearableFilters,
    allwaysShowSelectionActions,
    ...restProps
  }: TableProps<T>) => {
  const appConfig = useContext(AppConfigContext);
  const { push } = useContext(SnackbarContext);

  const [ isBulkOpsEnabled, setIsBulkOpsEnabled ] = useState(defaultIsBulkOpsEnabled);

  //default column
  const extendedDefaultColumn = useMemo(
    () => ({
      Cell: (
        {
          value,
          bodyCellProps,
        }: any) => value === undefined ? null :
        <span {...bodyCellProps} title={value}>{value}</span>,
      minWidth: 40,
      filter: (x: any) => x,
      canExportToExcel: true,
      excelProps: {
        filterButton: true,
      },
      ...defaultColumn,
    }),
    [ defaultColumn ],
  );

  //load fetchingParams
  const initialFetchingParams = useMemo(() => {
    return JSON.parse(window.sessionStorage.getItem(`mimic.tables.${tableId}`) || "{}");
  }, [ tableId ]);


  const columns = useMemo(() => {
    const columns = declaredColumns
      .map((column) => ({
        ...column,
        accessor: column.accessor ? (
          column.accessor.hasOwnProperty("path")
            ? column.accessor.toString()
            : column.accessor
        ) : column.accessor,
      })) as BaseColumn<T>[];

    const isColumnEqual = (c: BaseColumn<T>, ec: ExcludedColumn) => {
      const excludedId = ec.id;
      const excludedAccessor = ec.accessor ? (
        ec.accessor.hasOwnProperty("path")
          ? ec.accessor.toString()
          : ec.accessor
      ) : ec.accessor;

      return (c.id !== undefined && c.id === excludedId) || (c.accessor !== undefined && c.accessor === excludedAccessor);
    };

    excludedColumns?.forEach(ec => {
      if (!columns.some(c => isColumnEqual(c, ec))) {
        console.error("Table with id:", tableId, "has wrong excluded column", ec);
      }
    });

    return columns.filter((c) => !excludedColumns?.find((ec) => isColumnEqual(c, ec)));
  }, [ declaredColumns, excludedColumns, tableId ]);

  const defaultHiddenColumns = useMemo(() => {
    return columns.filter((x: BaseColumnTypeAdditionalProps<T>) => x.hiddenByDefault)
      .map((column) => `${column.id || String(column.accessor)}`);
  }, [ columns ]);

  const tableInstance = useTable(
    {
      columns: columns,
      data: data || [],
      initialState: {
        pageSize: 10,
        sortBy: defaultSortBy,
        ...initialFetchingParams,
        filters: initialFetchingParams?.filters || defaultFilters || [],
        hiddenColumns: defaultHiddenColumns,
      },
      manualPagination: true,
      manualSortBy,
      manualFilters,
      defaultCanFilter: true,
      autoResetSortBy,
      autoResetFilters: false,
      pageCount: controlledPageCount,
      allowRowSelect,
      defaultColumn: extendedDefaultColumn as BaseColumn<T>,
      autoResetSelectedRows: false,
      getRowId,
      totalCount,
      defaultFilters,
      unclearableFilters,
      pushNotification: push,
    },
    useFilters,
    useSortBy,
    usePagination,
    useResizeColumns,
    useGridLayout,
    useRowSelect,
    useColumnOrder,
    useCustomTableFilters,
    hooks => {
      if (isBulkOpsEnabled) {
        hooks.visibleColumns.push(columns => [
          SelectionColumn as any,
          ...columns,
        ]);
      }
    },
  );

  useRefEffect(tableRef, tableInstance);

  const {
    getTableProps,
    headerGroups,
    footerGroups,
    prepareRow,
    page,
    pageCount,
    gotoPage,
    setPageSize,
    selectedFlatRows,
    visibleColumns,
    setColumnOrder,
    allColumns,
    setAllFilters,
    setHiddenColumns,
    setSortBy,
    resetResizing,
    setResizing,
    toggleAllRowsSelected,
    columns: originalColumns,
    state: {
      pageIndex,
      pageSize,
      sortBy,
      filters,
      columnOrder,
      columnResizing: { columnWidths },
      hiddenColumns,
      selectedRowIds,
    },
  } = tableInstance;

  useEffect(() => {
    onColumnsChanged?.(allColumns);
  }, [ onColumnsChanged, allColumns ]);

  // hotfix for enabling allowRowSelect
  useEffect(
    () => {
      setColumnOrder(visibleColumns.map(d => d.id));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ isBulkOpsEnabled ],
  );

  useEffect(() => {
    if (selectedRowIds?.count !== undefined) {
      onSelectedRowCountChanged?.(selectedRowIds.count);
    }
  }, [ onSelectedRowCountChanged, selectedRowIds?.count ]);

  const userSettings = useContext(UserSettingContext);

  //load viewParams
  const initialViewParams = useMemo(
    () => {
      const {
        columnWidths,
        ...restProps
      } = userSettings.settings[`mimic.tables.${tableId}`] || {};
      return {
        ...restProps,
        columnResizing: { columnWidths },
      };
    },
    [ userSettings, tableId ],
  );
  useEffect(() => {
    function setParams() {
      if (!!Object.keys(initialViewParams.columnResizing?.columnWidths || {}).length) {
        setResizing(initialViewParams.columnResizing);
      }
      if (initialViewParams.columnOrder) {
        setColumnOrder([ ...initialViewParams.columnOrder ]);
      }
      if (initialViewParams.hiddenColumns) {
        setHiddenColumns(initialViewParams.hiddenColumns);
      }
    }

    // if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
    //   setParams();
    // } else {
    //   flushSync(setParams);
    // }
    try {
      if (!(!process.env.NODE_ENV || process.env.NODE_ENV === "development")) {
        setTimeout(() => {
          flushSync(setParams);
        }, 0);
      }
      setParams();
    } catch (error) {
      console.error("Error setting initial table view parameters:", error);
    }
  }, [ setResizing, setColumnOrder, setHiddenColumns, initialViewParams ]);

  // reset page on filters change
  useEffect(() => {
    if (filters !== initialFetchingParams.filters) {
      gotoPage(0);
    }
  }, [ gotoPage, filters, initialFetchingParams ]);

  // reset page if more than available
  useEffect(() => {
    if (!loading && !page.length) {
      if (pageCount && pageCount <= pageIndex) {
        gotoPage(pageCount - 1);
      }
    }
  }, [ gotoPage, pageIndex, pageCount, loading, page.length ]);

  const fetchingParams = useMemo((): Omit<FetchTableDataProps<T>, "columns"> => ({
    pageIndex,
    pageSize,
    sortBy,
    filters,
  }), [ pageIndex, pageSize, sortBy, filters ]);

  // saving fetchingParams to sessionStorage
  const debouncedFetchingParams = useDebounce(fetchingParams, 300);
  useEffect(() => {
    if (keepFetchingParams) {
      window.sessionStorage.setItem(`mimic.tables.${tableId}`, JSON.stringify(debouncedFetchingParams));
    }
  }, [ tableId, debouncedFetchingParams, keepFetchingParams ]);
  useEffect(() => {
    if (!keepFetchingParams) {
      window.sessionStorage.removeItem(`mimic.tables.${tableId}`);
    }
  }, [ tableId, keepFetchingParams ]);

  // request data from server
  const debouncedFetchTableDataProps = useDebounce(fetchingParams, debounceTimeout);
  useEffect(() => fetchData?.(debouncedFetchTableDataProps), [ fetchData, debouncedFetchTableDataProps ]);

  // filters
  const isFiltersApplied = !!filters?.filter(f => Array.isArray(f.value) ? f.value.length : (f.value !== undefined && f.value !== "")).length;
  const clearFilters = useCallback(() => setAllFilters([]), [ setAllFilters ]);
  const isDefaultFiltersExists = useMemo(() => !!defaultFilters?.filter(f => Array.isArray(f.value) ? f.value.length : f.value !== undefined).length, [ defaultFilters ]);
  const setDefaultFilters = useCallback(() => setAllFilters(defaultFilters || []), [ setAllFilters, defaultFilters ]);
  const setDefaultFiltersVisible = useMemo(() => isDefaultFiltersExists && !equal(filters, defaultFilters), [ isDefaultFiltersExists, filters, defaultFilters ]);

  // columns
  const activeColumns = useMemo(
    () => allColumns.filter((x: any) => x !== SelectionColumn),
    [ allColumns ],
  );
  const columnOrderChange = useCallback(
    (newArr: any) => setColumnOrder(newArr.map((item: { id: string; }) => item.id)),
    [ setColumnOrder ],
  );

  // reset settings
  const resetSort = useCallback(
    () => setSortBy(defaultSortBy || []),
    [ setSortBy, defaultSortBy ],
  );
  const resetColumnOrder = useCallback(
    () => setColumnOrder(originalColumns.map(c => c.id)),
    [ originalColumns, setColumnOrder ],
  );
  const resetColumnVisibility = useCallback(
    () => setHiddenColumns(defaultHiddenColumns || []),
    [ setHiddenColumns, defaultHiddenColumns ],
  );
  const resetViewSettings = useCallback(() => {
    resetSort();
    resetColumnOrder();
    resetColumnVisibility();
    resetResizing();
  }, [ resetColumnOrder, resetColumnVisibility, resetSort, resetResizing ]);

  // saving view settings
  const viewSettings = useMemo(() => ({
    columnOrder,
    hiddenColumns,
    columnWidths,
  }), [ columnOrder, hiddenColumns, columnWidths ]);
  const debouncedViewSettings = useDebounce(viewSettings, 300);
  useEffect(() => {
    const defaultSettings = {
      columnOrder: [],
      hiddenColumns: defaultHiddenColumns || [],
      columnWidths: {},
    };
    const storedSettings = userSettings.settings[`mimic.tables.${tableId}`];
    if (!isEqual(debouncedViewSettings, defaultSettings)) {
      if (!isEqual(debouncedViewSettings, storedSettings)) {
        userSettings.settings[`mimic.tables.${tableId}`] = debouncedViewSettings;
        if (userSettings.saveSetting && userSettings.initialized) {
          userSettings.saveSetting(`mimic.tables.${tableId}`, debouncedViewSettings);
        }
      }
    }
  }, [ tableId, userSettings, debouncedViewSettings, defaultHiddenColumns ]);

  const showSelectionActions = allwaysShowSelectionActions || (allowRowSelect && !!selectedRowIds.count);


  //// Excel block
  const toExcelGetColumns = useCallback((): MimicExcelColumn<T>[] => {
    return (allColumns as (ColumnInstance<T> & Column<T>)[])
      ?.filter(column => column.canExportToExcel)
      ?.filter(column => column.isVisible)
      ?.map(column => ({
        name: `${column.Header}`,
        cellAccessor: (row) => {
          const accessor = column.accessor ? (
            column.accessor.hasOwnProperty("path")
              ? column.accessor.toString()
              : column.accessor
          ) : column.accessor;

          try {
            const cellEntity = typeof accessor === "string" ? get(row, accessor) : (accessor as any)(row);
            return column.excelProps?.valueModifier ? column.excelProps.valueModifier(cellEntity, appConfig) : cellEntity;
          } catch {
            throw new Error("Unsupported excel accessor");
          }
        },
        width: ((column.width as number) / 10),
        ...column.excelProps,
      })) || [];
  }, [ allColumns, appConfig ]);
  const toExcelProps: ExportToExcelHookProps<T> = {
    getItems: toExcelGetItems || useCallback(() => data, [ data ]),
    getColumns: toExcelGetColumns,
    fileName: tableId,
    additionalProps: createExcelProps,
  };
  const toExcel = useExportToExcel<T>(toExcelProps);
  ////

  return (
    <DisabledContextProvider value={disabled || false}>
      <div className={classNames(styles.root, className, { [styles.withSelectionActions]: showSelectionActions })}>
        <SortingContext.Provider value={sortBy}>
          <VisibleColumnsContext.Provider value={visibleColumns}>
            {
              showSelectionActions ?
                <SelectionActions
                  selectedRowIds={selectedRowIds}
                  total={totalCount}
                  className={selectionActionsClassName}
                  toggleAllRowsSelected={toggleAllRowsSelected}
                  entityName={selectionEntityName}
                  showSelectedAll={selectionShowSelectedAll}
                  showSelectedItemsCount={selectionShowSelectedItemsCount}
                >
                  {selectionActions}
                </SelectionActions>
                :
                <TableActions className={styles.actions}>
                  <div className={styles.filters}>
                    <ClearFilters
                      isVisible={isFiltersApplied}
                      clearFilters={clearFilters}
                    />
                    <SetDefaultFilters
                      isVisible={setDefaultFiltersVisible}
                      clearFilters={setDefaultFilters}
                    />
                  </div>
                  {tableActions}
                  <ExportToExcel
                    isVisible={allowExportToExcel! && !!totalCount}
                    onClick={toExcel}
                  />
                  <BulkOps
                    isVisible={allowRowSelect || false}
                    setIsBulkOpsEnabled={setIsBulkOpsEnabled}
                    isBulkOpsEnabled={isBulkOpsEnabled}
                  />
                  {!hideSettings &&
                    <Settings
                      resetViewSettings={resetViewSettings}
                      columns={activeColumns}
                      columnOrderChange={columnOrderChange}
                    />
                  }
                </TableActions>
            }
            <Table
              page={page}
              selectedFlatRows={selectedFlatRows}
              prepareRow={prepareRow}
              allowRowSelect={allowRowSelect}
              headerGroups={headerGroups}
              footerGroups={footerGroups}
              showFooter={showFooter}
              loading={loading}
              filters={filters}
              pageSize={pageSize}
              className={classNames(styles.table, tableClassName)}
              {...getTableProps()}
              {...restProps}
            />
          </VisibleColumnsContext.Provider>
        </SortingContext.Provider>
        <Pager
          hidden={disablePager || (loading === false && !page.length)}
          className={classNames({ [styles.pagerDisabled]: disablePager })}
          pageCount={pageCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          gotoPage={gotoPage}
          setPageSize={setPageSize}
          role="pager"
        />
      </div>
    </DisabledContextProvider>
  );
});

export default TableContainer;
